import React, { useState } from 'react';
import { Package, Clock, CheckCircle2, AlertCircle, ArrowRight, FileText, Video, History, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { usePackages, ReportPackage } from '../../contexts/PackagesContext';
import { ZodiacTheme } from './zodiacThemes';
interface PackagesTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
export function PackagesTab({
  theme,
  onNavigate
}: PackagesTabProps) {
  const {
    packages,
    activePackages,
    totalRemaining,
    totalUsed
  } = usePackages();
  const [expandedPackageId, setExpandedPackageId] = useState<string | null>(null);
  const toggleExpand = (id: string) => {
    setExpandedPackageId(expandedPackageId === id ? null : id);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatPrice = (price: number) => {
    return price.toLocaleString('ru-RU') + ' ₽';
  };
  const getStatusBadge = (status: ReportPackage['status']) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" data-id="element-19958">Активен</Badge>;
      case 'exhausted':
        return <Badge variant="neutral" data-id="element-19959">Закончился</Badge>;
      case 'frozen':
        return <Badge variant="warning" data-id="element-19960">Заморожен</Badge>;
      case 'cancelled':
        return <Badge variant="neutral" data-id="element-19961">Отменен</Badge>;
    }
  };
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-19962">
      {/* 1. ВЕРХНЯЯ СВОДКА */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-19963">
        <Card className="p-5 border-slate-200" data-id="element-19964">
          <div className="flex items-center gap-3 mb-3" data-id="element-19965">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white shadow-sm`} data-id="element-19966">
              <Package className="w-5 h-5" data-id="element-19967" />
            </div>
            <div data-id="element-19968">
              <div className="text-sm font-medium text-slate-500" data-id="element-19969">
                Активные пакеты
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-19970">
                {activePackages.length}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-19971">
            Всего отчетов в пакетах:{' '}
            {packages.reduce((sum, p) => sum + p.totalReports, 0)}
          </p>
        </Card>

        <Card className="p-5 border-slate-200" data-id="element-19972">
          <div className="flex items-center gap-3 mb-3" data-id="element-19973">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm" data-id="element-19974">
              <CheckCircle2 className="w-5 h-5" data-id="element-19975" />
            </div>
            <div data-id="element-19976">
              <div className="text-sm font-medium text-slate-500" data-id="element-19977">
                Осталось отчетов
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-19978">
                {totalRemaining}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-19979">Готовы к использованию</p>
        </Card>

        <Card className="p-5 border-slate-200" data-id="element-19980">
          <div className="flex items-center gap-3 mb-3" data-id="element-19981">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm" data-id="element-19982">
              <History className="w-5 h-5" data-id="element-19983" />
            </div>
            <div data-id="element-19984">
              <div className="text-sm font-medium text-slate-500" data-id="element-19985">
                Использовано
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-19986">
                {totalUsed}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-19987">Отчетов списано из пакетов</p>
        </Card>
      </div>

      {/* 2. СПИСОК ПАКЕТОВ */}
      <div className="flex items-center justify-between" data-id="element-19988">
        <h2 className="text-lg font-bold text-slate-900" data-id="element-19989">Мои пакеты</h2>
        <Button variant="primary" size="sm" onClick={() => onNavigate('pricing')} data-id="element-19990">
          Купить еще
        </Button>
      </div>

      {packages.length === 0 ? <Card className="p-12 text-center border-slate-200 border-dashed" data-id="element-19991">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-19992" />
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-19993">
            У вас пока нет пакетов
          </h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto" data-id="element-19994">
            Покупайте пакеты отчетов со скидкой до 30% и используйте их для
            оплаты любых услуг платформы.
          </p>
          <Button variant="primary" onClick={() => onNavigate('pricing')} data-id="element-19995">
            Выбрать пакет
          </Button>
        </Card> : <div className="space-y-4" data-id="element-19996">
          {packages.map(pkg => {
        const isExpanded = expandedPackageId === pkg.id;
        const progress = pkg.usedReports / pkg.totalReports * 100;
        return <Card key={pkg.id} className="border-slate-200 overflow-hidden transition-all duration-200 hover:shadow-md" data-id="element-19997">
                {/* Package Header (Always visible) */}
                <div className="p-5 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4" onClick={() => toggleExpand(pkg.id)} data-id="element-19998">
                  <div className="flex items-start gap-4" data-id="element-19999">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white shadow-sm flex-shrink-0`} data-id="element-20000">
                      <Package className="w-6 h-6" data-id="element-20001" />
                    </div>
                    <div data-id="element-20002">
                      <div className="flex items-center gap-2 mb-1" data-id="element-20003">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-20004">
                          {pkg.title}
                        </h3>
                        {getStatusBadge(pkg.status)}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-3" data-id="element-20005">
                        <span data-id="element-20006">Куплен: {formatDate(pkg.purchaseDate)}</span>
                        <span className="hidden sm:inline" data-id="element-20007">•</span>
                        <span className="hidden sm:inline" data-id="element-20008">
                          {formatPrice(pkg.purchasePrice)} (
                          {formatPrice(pkg.pricePerReport)} / шт)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:justify-end gap-6 md:w-1/3" data-id="element-20009">
                    <div className="flex-grow md:flex-grow-0 md:w-48" data-id="element-20010">
                      <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-20011">
                        <span className="text-slate-500" data-id="element-20012">
                          Использовано: {pkg.usedReports}
                        </span>
                        <span className="text-slate-900" data-id="element-20013">
                          Осталось: {pkg.remainingReports}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-20014">
                        <div className={`h-full bg-gradient-to-r ${theme.gradient} rounded-full transition-all duration-500`} style={{
                    width: `${progress}%`
                  }} data-id="element-20015" />
                      </div>
                    </div>
                    <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-50" data-id="element-20016">
                      {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-20017" /> : <ChevronDown className="w-5 h-5" data-id="element-20018" />}
                    </button>
                  </div>
                </div>

                {/* Package Details (Expanded) */}
                {isExpanded && <div className="px-5 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50" data-id="element-20019">
                    <div className="flex items-center justify-between mb-4" data-id="element-20020">
                      <h4 className="text-sm font-bold text-slate-900" data-id="element-20021">
                        История использования
                      </h4>
                      {pkg.remainingReports > 0 && <Button variant="outline" size="sm" onClick={() => onNavigate('services')} data-id="element-20022">
                          Оформить новый заказ
                        </Button>}
                    </div>

                    {pkg.deductions.length === 0 ? <div className="text-center py-6 bg-white rounded-xl border border-slate-200 border-dashed" data-id="element-20023">
                        <p className="text-sm text-slate-500" data-id="element-20024">
                          Вы еще не использовали отчеты из этого пакета
                        </p>
                      </div> : <div className="space-y-3" data-id="element-20025">
                        {pkg.deductions.map(deduction => <div key={deduction.id} className="flex items-center justify-between p-3 sm:p-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-20026">
                            <div className="flex items-center gap-3" data-id="element-20027">
                              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-20028">
                                {deduction.source === 'ai' ? <FileText className="w-4 h-4" data-id="element-20029" /> : <Video className="w-4 h-4" data-id="element-20030" />}
                              </div>
                              <div data-id="element-20031">
                                <div className="text-sm font-bold text-slate-900" data-id="element-20032">
                                  {deduction.reportTitle}
                                </div>
                                <div className="text-xs text-slate-500 flex items-center gap-2" data-id="element-20033">
                                  <span data-id="element-20034">{formatDate(deduction.date)}</span>
                                  <span data-id="element-20035">•</span>
                                  <span data-id="element-20036">Заказ #{deduction.orderId}</span>
                                  {deduction.astrologerName && <>
                                      <span data-id="element-20037">•</span>
                                      <span className="text-indigo-600" data-id="element-20038">
                                        {deduction.astrologerName}
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-bold text-rose-500 whitespace-nowrap" data-id="element-20039">
                              -1 отчёт
                            </div>
                          </div>)}
                      </div>}
                  </div>}
              </Card>;
      })}
        </div>}
    </div>;
}